/**
 * FlightDataTransformer - Normalizes flight data from different providers
 * into a consistent format compatible with the existing flight processing logic
 */
export class FlightDataTransformer {
    /**
     * Normalize FlightApi.io response into the shape the app expects.
     * The API returns itineraries, legs, and segments at the top level.
     * selectBestFlights expects resp.data.itineraries, resp.data.legs, etc.,
     * so this returns { itineraries, legs, segments } (the caller places
     * the result into resp.data).
     *
     * @param {Object} response - Raw FlightApi.io response body
     * @returns {Object} { itineraries, legs, segments }
     */
    normalizeFlightApiResponse(response) {
        // If itineraries are at the top level, return the shape directly
        if (Array.isArray(response?.itineraries)) {
            return {
                itineraries: response.itineraries,
                legs: response.legs || [],
                segments: response.segments || []
            };
        }

        // If nested under a data key (legacy format), unwrap it
        if (Array.isArray(response?.data?.itineraries)) {
            return {
                itineraries: response.data.itineraries,
                legs: response.data.legs || [],
                segments: response.data.segments || []
            };
        }

        // Unrecognized format — return as-is and let validation catch it
        return response;
    }

    /**
     * Convert Amadeus API response to FlightApi.io compatible format
     * @param {Object} amadeusResponse - Raw Amadeus API response
     * @returns {Object} Normalized response in FlightApi.io format
     */
    normalizeAmadeusResponse(amadeusResponse) {
        if (!amadeusResponse || !amadeusResponse.data) {
            throw new Error('Invalid Amadeus response: missing data');
        }

        const { data: flightOffers, dictionaries } = amadeusResponse;

        if (!Array.isArray(flightOffers)) {
            throw new Error('Invalid Amadeus response: data is not an array');
        }

        const legs = [];
        const segments = [];
        const itineraries = [];

        flightOffers.forEach((offer, offerIndex) => {
            try {
                const legIds = [];

                // Process each itinerary (outbound/inbound)
                offer.itineraries.forEach((itinerary, itinIndex) => {
                    const legId = `${offerIndex}-${itinIndex}`;
                    const segmentIds = [];

                    // Process each segment within the itinerary
                    itinerary.segments.forEach((segment, segIndex) => {
                        const segmentId = `${offerIndex}-${itinIndex}-${segIndex}`;

                        segments.push({
                            id: segmentId,
                            duration: this.convertDurationToMinutes(segment.duration),
                            departure: {
                                iataCode: segment.departure.iataCode,
                                at: segment.departure.at,
                                terminal: segment.departure.terminal
                            },
                            arrival: {
                                iataCode: segment.arrival.iataCode,
                                at: segment.arrival.at,
                                terminal: segment.arrival.terminal
                            },
                            carrierCode: segment.carrierCode,
                            number: segment.number,
                            aircraft: segment.aircraft
                        });

                        segmentIds.push(segmentId);
                    });

                    // Create leg (a group of segments that form one direction of travel)
                    legs.push({
                        id: legId,
                        stop_count: Math.max(0, itinerary.segments.length - 1), // Number of stops = segments - 1
                        segment_ids: segmentIds
                    });

                    legIds.push(legId);
                });

                // Create itinerary (complete round-trip or one-way journey)
                itineraries.push({
                    leg_ids: legIds,
                    cheapest_price: {
                        amount: parseFloat(offer.price.total),
                        currency: offer.price.currency
                    }
                });
            } catch (error) {
                console.warn(`Skipping malformed flight offer ${offerIndex}:`, error);
                // Continue processing other offers
            }
        });

        return {
            data: {
                itineraries,
                legs,
                segments
            }
        };
    }

    /**
     * Convert ISO 8601 duration (PT2H10M) to minutes
     * @param {string} isoDuration - Duration in ISO 8601 format
     * @returns {number} Duration in minutes
     */
    convertDurationToMinutes(isoDuration) {
        if (!isoDuration) return 0;

        // Match PT2H10M format
        const matches = isoDuration.match(/PT(?:(\d+)H)?(?:(\d+)M)?/);
        if (!matches) {
            console.warn('Invalid duration format:', isoDuration);
            return 0;
        }

        const hours = parseInt(matches[1] || 0);
        const minutes = parseInt(matches[2] || 0);
        return hours * 60 + minutes;
    }

    /**
     * Validate normalized response structure
     * @param {Object} response - Normalized response to validate
     * @returns {boolean} True if valid
     */
    validateNormalizedResponse(response) {
        try {
            const { data } = response;

            if (!data) return false;
            if (!Array.isArray(data.itineraries)) return false;
            if (!Array.isArray(data.legs)) return false;
            if (!Array.isArray(data.segments)) return false;

            // Check that all itineraries have valid structure
            for (const itinerary of data.itineraries) {
                if (!itinerary.leg_ids || !Array.isArray(itinerary.leg_ids)) return false;
                if (!itinerary.cheapest_price || typeof itinerary.cheapest_price.amount !== 'number') return false;
            }

            // Check that all legs have valid structure
            for (const leg of data.legs) {
                if (!leg.id || typeof leg.stop_count !== 'number') return false;
                if (!leg.segment_ids || !Array.isArray(leg.segment_ids)) return false;
            }

            // Check that all segments have valid structure
            for (const segment of data.segments) {
                if (!segment.id || typeof segment.duration !== 'number') return false;
            }

            return true;
        } catch (error) {
            console.error('Error validating response:', error);
            return false;
        }
    }

    /**
     * Get transformation statistics for debugging
     * @param {Object} originalResponse - Original API response
     * @param {Object} normalizedResponse - Normalized response
     * @returns {Object} Transformation statistics
     */
    getTransformationStats(originalResponse, normalizedResponse) {
        const stats = {
            provider: 'unknown',
            originalOffers: 0,
            normalizedItineraries: 0,
            normalizedLegs: 0,
            normalizedSegments: 0,
            transformationTime: Date.now()
        };

        try {
            if (originalResponse.data && Array.isArray(originalResponse.data)) {
                // Amadeus format
                stats.provider = 'amadeus';
                stats.originalOffers = originalResponse.data.length;
            } else if (originalResponse.data && originalResponse.data.itineraries) {
                // FlightApi.io format (wrapped in data key)
                stats.provider = 'flightapi';
                stats.originalOffers = originalResponse.data.itineraries.length;
            } else if (Array.isArray(originalResponse.itineraries)) {
                // FlightApi.io format (unwrapped / new format)
                stats.provider = 'flightapi';
                stats.originalOffers = originalResponse.itineraries.length;
            }

            if (normalizedResponse.data) {
                stats.normalizedItineraries = normalizedResponse.data.itineraries?.length || 0;
                stats.normalizedLegs = normalizedResponse.data.legs?.length || 0;
                stats.normalizedSegments = normalizedResponse.data.segments?.length || 0;
            }
        } catch (error) {
            console.warn('Error calculating transformation stats:', error);
        }

        return stats;
    }
}